/* eslint-disable camelcase */
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { ArticlesService } from '../../_generated/api/articles.service';
import { ArticleSummaryView } from '../../_generated/model/articleSummaryView';
import { ArticleView } from '../../_generated/model/articleView';
import { ArticleYearlyBreakdownView } from '../../_generated/model/articleYearlyBreakdownView';
import { ArticlesActions } from './articles.actions';
import { ArticlesState } from './articles.reducer';

@Injectable()
export class ArticlesEffects {
  private readonly actions$ = inject(Actions);
  private readonly articlesService = inject(ArticlesService);
  private readonly store = inject(Store<ArticlesState>);

  loadArticles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesActions.loadArticles),
      exhaustMap(({ year }) =>
        this.articlesService.articlesControllerGetArticlesByYear(year).pipe(
          map(
            (
              apiResponse:
                | ArticleSummaryView[]
                | { data: ArticleSummaryView[] },
            ) => {
              // Handle both old format (ArticleView[]) and new format (PaginatedArticlesView)
              const apiArticles =
                'data' in apiResponse ? apiResponse.data : apiResponse;

              return ArticlesActions.loadArticlesSuccess({
                articles: apiArticles,
              });
            },
          ),
          catchError((error: Error) =>
            of(
              ArticlesActions.loadArticlesFailure({
                error: error.message || 'Failed to load articles',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  selectYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesActions.selectYear),
      switchMap(({ year }) => [ArticlesActions.loadArticles({ year })]),
    ),
  );

  loadArticlesYearlyBreakdown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesActions.loadArticlesYearlyBreakdown),
      exhaustMap(() =>
        this.articlesService.articlesControllerGetYearlyBreakdown().pipe(
          map((articlesYearlyBreakdown: ArticleYearlyBreakdownView[]) =>
            ArticlesActions.loadArticlesYearlyBreakdownSuccess({
              articlesYearlyBreakdown,
            }),
          ),
          catchError((error: Error) =>
            of(
              ArticlesActions.loadArticlesYearlyBreakdownFailure({
                error:
                  error.message || 'Failed to load articles yearly breakdown',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadArticleById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesActions.loadArticleByNodeId),
      exhaustMap(({ nodeId }) =>
        this.articlesService.articlesControllerGetArticle(nodeId).pipe(
          map((apiArticle: ArticleView) => {
            return ArticlesActions.loadArticleByNodeIdSuccess({
              article: apiArticle,
            });
          }),
          catchError((error: Error) =>
            of(
              ArticlesActions.loadArticleByNodeIdFailure({
                error: error.message || 'Failed to load article',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadLatestArticle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesActions.loadLatestArticle),
      exhaustMap(() =>
        this.articlesService.articlesControllerGetLatestArticle().pipe(
          map((apiArticle: ArticleView) => {
            return ArticlesActions.loadLatestArticleSuccess({
              article: apiArticle,
            });
          }),
          catchError((error: Error) =>
            of(
              ArticlesActions.loadLatestArticleFailure({
                error: error.message || 'Failed to load latest article',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadMoreArticles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesActions.loadMoreArticles),
      exhaustMap(({ year, page }) =>
        this.articlesService
          .articlesControllerGetArticlesByYear(year, page, 50)
          .pipe(
            map(
              (
                apiResponse:
                  | ArticleView[]
                  | {
                      data: ArticleSummaryView[];
                      meta: { hasNextPage: boolean };
                    },
              ) => {
                // Handle both old format (ArticleView[]) and new format (PaginatedArticlesView)
                const apiArticles =
                  'data' in apiResponse ? apiResponse.data : apiResponse;
                const hasMorePages =
                  'meta' in apiResponse ? apiResponse.meta.hasNextPage : false;

                return ArticlesActions.loadMoreArticlesSuccess({
                  articles: apiArticles,
                  hasMorePages,
                });
              },
            ),
            catchError((error: Error) =>
              of(
                ArticlesActions.loadMoreArticlesFailure({
                  error: error.message || 'Failed to load more articles',
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
